import {
  AimOutlined,
  EyeOutlined,
  RightOutlined,
} from "@ant-design/icons";

import type { LayerKey, StormPoint } from "../domain/types";

interface TyphoonRailProps {
  currentPoint: StormPoint | null;
  hiddenLayers: LayerKey[];
  onToggleLayer: (layer: LayerKey) => void;
}

const layers: Array<{ key: LayerKey; label: string; swatch: string }> = [
  { key: "observed", label: "实况路径", swatch: "line-observed" },
  { key: "forecast", label: "预测路径", swatch: "line-forecast" },
  { key: "wind12", label: "中心风圈", swatch: "ring-12" },
  { key: "wind10", label: "10级风圈", swatch: "ring-10" },
  { key: "wind7", label: "7级风圈", swatch: "ring-7" },
  { key: "rain", label: "降雨预估", swatch: "rain" },
  { key: "cities", label: "城市标注", swatch: "city" },
];

function beaufortFromKph(speed: number) {
  const limits = [1, 6, 12, 20, 29, 39, 50, 62, 75, 89, 103, 118, 133, 150, 167, 184, 202];
  const found = limits.findIndex((limit) => speed < limit);
  return found === -1 ? 17 : found;
}

function radiiText(point: StormPoint, windScale: 7 | 10 | 12) {
  const radius = point.windRadii.find((ring) => ring.windScale === windScale);
  if (!radius) return "—";
  const { northeast, southeast, southwest, northwest } = radius.quadrantsKm;
  return `东北 ${Math.round(northeast)} · 东南 ${Math.round(southeast)} · 西南 ${Math.round(southwest)} · 西北 ${Math.round(northwest)} km`;
}

export function TyphoonRail({ currentPoint, hiddenLayers, onToggleLayer }: TyphoonRailProps) {
  return (
    <div className="typhoon-rail">
      <section className="rail-panel glass-panel" aria-label="台风信息">
        <header className="rail-panel__header">
          <span><AimOutlined aria-hidden />台风信息</span><RightOutlined aria-hidden />
        </header>
        {currentPoint ? (
          <dl className="storm-stats">
            <div><dt>中心位置</dt><dd>{currentPoint.latitude.toFixed(1)}°N {currentPoint.longitude.toFixed(1)}°E</dd></div>
            <div><dt>强度等级</dt><dd>{currentPoint.intensity ?? "—"}</dd></div>
            <div><dt>中心气压</dt><dd>{currentPoint.pressureHpa ? `${Math.round(currentPoint.pressureHpa)} hPa` : "—"}</dd></div>
            <div><dt>最大风速</dt><dd>{currentPoint.windSpeedKph ? `${Math.round(currentPoint.windSpeedKph)} km/h（${beaufortFromKph(currentPoint.windSpeedKph)}级）` : "—"}</dd></div>
            <div><dt>有效时间</dt><dd>{formatTime(currentPoint.time)}</dd></div>
            <div className="storm-stats__wide"><dt>中心风圈半径</dt><dd>{radiiText(currentPoint, 12)}</dd></div>
            <div className="storm-stats__wide"><dt>十级风圈半径</dt><dd>{radiiText(currentPoint, 10)}</dd></div>
            <div className="storm-stats__wide"><dt>七级风圈半径</dt><dd>{radiiText(currentPoint, 7)}</dd></div>
          </dl>
        ) : <div className="panel-empty">当前暂无台风信息</div>}
      </section>
      <section className="rail-panel rail-panel--layers glass-panel" aria-label="图层筛选">
        <header className="rail-panel__header"><span><EyeOutlined aria-hidden />图层筛选</span></header>
        <div className="layer-list">
          {layers.map((layer) => {
            const enabled = !hiddenLayers.includes(layer.key);
            return (
              <div className="layer-row" key={layer.key}>
                <i aria-hidden className={`layer-swatch layer-swatch--${layer.swatch}`} />
                <span>{layer.label}</span>
                <button
                  aria-checked={enabled}
                  aria-label={layer.label}
                  className="layer-switch"
                  onClick={() => onToggleLayer(layer.key)}
                  role="switch"
                  type="button"
                ><i /></button>
              </div>
            );
          })}
        </div>
      </section>
    </div>
  );
}

function formatTime(time: string) {
  return new Intl.DateTimeFormat("zh-CN", {
    timeZone: "Asia/Shanghai",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(new Date(time));
}
